import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { custOrders, custInvoices, orderPdfUrl, payOrderRedsys, downloadBlob } from "../lib/api";
import Seo from "../components/Seo";
import { eur, STATUS_LABELS, STATUS_COLORS } from "../lib/format";
import { LogOut, Download, Package, FileText, User, CreditCard } from "lucide-react";
import { toast } from "sonner";

export default function CustomerAccount() {
  const { customer, loading, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    if (!loading && !customer) nav("/acceso", { state: { from: "/mi-cuenta" } });
  }, [loading, customer, nav]);

  useEffect(() => {
    if (customer) {
      custOrders().then(setOrders).catch(() => {});
      custInvoices().then(setInvoices).catch(() => {});
    }
  }, [customer]);

  if (!customer) return <div className="max-w-4xl mx-auto px-4 py-20"><div className="h-40 bg-secondary animate-pulse rounded-sm" /></div>;

  const retryPayment = async (o) => {
    try {
      const { url, fields } = await payOrderRedsys(o.id);
      const f = document.createElement("form");
      f.method = "POST";
      f.action = url;
      Object.entries(fields).forEach(([n, v]) => {
        const inp = document.createElement("input");
        inp.type = "hidden"; inp.name = n; inp.value = v;
        f.appendChild(inp);
      });
      document.body.appendChild(f);
      f.submit();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo iniciar el pago");
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      <Seo title="Mi cuenta | FABRIPAN" description="Área de cliente FABRIPAN." noindex />
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-3xl tracking-tighter">Mi cuenta</h1>
          <p className="text-muted-foreground text-sm flex items-center gap-1.5 mt-1"><User className="h-4 w-4" /> {customer.name} · {customer.email}</p>
        </div>
        <button onClick={() => { logout(); nav("/"); }} className="inline-flex items-center gap-2 text-sm border border-border rounded-sm px-4 py-2 hover:border-destructive hover:text-destructive transition-colors" data-testid="logout-btn"><LogOut className="h-4 w-4" /> Salir</button>
      </div>

      <div className="flex gap-2 mb-5">
        <button onClick={() => setTab("orders")} className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-sm transition-colors ${tab === "orders" ? "bg-brand text-white" : "bg-secondary text-muted-foreground"}`} data-testid="tab-orders"><Package className="h-4 w-4" /> Pedidos</button>
        <button onClick={() => setTab("invoices")} className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-sm transition-colors ${tab === "invoices" ? "bg-brand text-white" : "bg-secondary text-muted-foreground"}`} data-testid="tab-invoices"><FileText className="h-4 w-4" /> Facturas</button>
      </div>

      {tab === "orders" ? (
        orders.length === 0 ? <p className="text-muted-foreground py-16 text-center">Aún no tiene pedidos.</p> : (
          <div className="space-y-3">
            {orders.map((o) => (
              <div key={o.id} className="border border-border rounded-sm bg-white p-4" data-testid={`account-order-${o.number}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="font-mono font-medium">{o.number}</p>
                    <p className="text-xs text-muted-foreground">{o.date} · {o.items.length} artículos</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-medium px-2 py-1 rounded-sm ${STATUS_COLORS[o.status] || "bg-secondary"}`}>{STATUS_LABELS[o.status] || o.status}</span>
                    <span className="font-heading font-bold text-brand">{eur(o.total)}</span>
                    <a href={orderPdfUrl(o.id)} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-brand" aria-label="PDF"><Download className="h-4 w-4" /></a>
                  </div>
                </div>
                {!o.paid && o.status === "pendiente" && (
                  <div className="mt-3 pt-3 border-t border-border flex justify-end">
                    <button onClick={() => retryPayment(o)} className="inline-flex items-center gap-2 bg-brand text-white text-sm rounded-sm px-4 py-2 hover:bg-brand-dark" data-testid={`retry-pay-${o.number}`}><CreditCard className="h-4 w-4" /> Pagar con tarjeta</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      ) : (
        invoices.length === 0 ? <p className="text-muted-foreground py-16 text-center">Aún no tiene facturas.</p> : (
          <div className="space-y-3">
            {invoices.map((i) => (
              <div key={i.id} className="border border-border rounded-sm bg-white p-4 flex items-center justify-between" data-testid={`account-invoice-${i.number}`}>
                <div><p className="font-mono font-medium">{i.number}</p><p className="text-xs text-muted-foreground">{i.date} · Pedido {i.order_number}</p></div>
                <div className="flex items-center gap-4">
                  <span className="font-heading font-bold text-brand">{eur(i.total)}</span>
                  <button type="button" onClick={() => downloadBlob(`/customer/invoices/${i.id}/pdf`, `${i.number}.pdf`)} className="inline-flex items-center gap-1 text-sm text-brand hover:underline" data-testid={`account-invoice-pdf-${i.number}`}><Download className="h-4 w-4" /> PDF</button>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
